import React from 'react';
import { Box, SxProps, Theme } from '@mui/material';
import { useStateBasedProps } from './useStateBasedProps';
import { TextProps, StateBasedProps } from '../Button/ButtonContent';

interface ButtonTextProps {
  text?: string | StateBasedProps<string>;
  textProps?: TextProps | StateBasedProps<TextProps>;
  isHovered: boolean;
  hoverEnabled: boolean;
  colorScheme?: string;
  hasMultipleItems: boolean;
  height: number;
}

export const ButtonText: React.FC<ButtonTextProps> = ({
  text,
  textProps,
  colorScheme,
  isHovered,
  hoverEnabled,
  hasMultipleItems,
  height,
}) => {
  const currentText = useStateBasedProps(
    typeof text === 'object' ? text : { default: text || '' },
    isHovered,
    hoverEnabled,
  );

  const currentTextProps = useStateBasedProps(textProps, isHovered, hoverEnabled) || {};

  const isDarkBackground = ['chalk', 'dark', 'halloween', 'purplePassion'].includes(
    colorScheme ?? '',
  );
  const fallbackColor = isDarkBackground ? '#808080' : '#808080';

  if (!currentText) return null;

  const getAlignItems = (alignSelf?: string) => {
    switch (alignSelf) {
      case 'flex-start':
        return 'flex-start';
      case 'center':
        return 'center';
      case 'flex-end':
        return 'flex-end';
      default:
        return 'center';
    }
  };

  const getJustifyContent = (justifySelf?: string) => {
    switch (justifySelf) {
      case 'flex-start':
        return 'flex-start';
      case 'center':
        return 'center';
      case 'flex-end':
        return 'flex-end';
      default:
        return 'center';
    }
  };

  const getTextAlign = (justifySelf?: string): 'left' | 'center' | 'right' => {
    switch (justifySelf) {
      case 'flex-start':
        return 'left';
      case 'center':
        return 'center';
      case 'flex-end':
        return 'right';
      default:
        return (currentTextProps.align as 'left' | 'center' | 'right') || 'center';
    }
  };

  const boxStyle: SxProps<Theme> = {
    display: 'flex',
    alignItems: getAlignItems(currentTextProps.alignSelf),
    justifyContent: getJustifyContent(currentTextProps.justifySelf),
    padding: currentTextProps.padding ?? 0,
    flex: hasMultipleItems ? '1 1 auto' : '1',
    minWidth: 0,
    height: '100%',
  };

  const textStyle: React.CSSProperties = {
    fontSize: currentTextProps.fontSize || 16,
    fontWeight: currentTextProps.fontWeight || 'normal',
    fontStyle: currentTextProps.fontStyle || 'normal',
    color: currentTextProps.color || fallbackColor,
    fontFamily: currentTextProps.fontFamily,
    margin: 0,
    lineHeight: 1.2,
    textAlign: getTextAlign(currentTextProps.justifySelf),
    overflow: 'hidden',
    wordBreak: 'break-word',
    width: '100%',
    whiteSpace: 'normal',
    display: 'block',
    transition: 'all 0.2s ease',
  };

  const fontSize = currentTextProps.fontSize || 16;
  const lineHeight = 1.2;
  const padding =
    typeof currentTextProps.padding === 'number'
      ? currentTextProps.padding
      : typeof currentTextProps.padding === 'string'
        ? parseInt(currentTextProps.padding) || 0
        : 0;
  const availableHeight = height - padding * 2;
  const maxLines = Math.floor(availableHeight / (fontSize * lineHeight));

  return (
    <Box sx={boxStyle}>
      <div
        style={{
          ...textStyle,
          display: '-webkit-box',
          WebkitBoxOrient: 'vertical',
          WebkitLineClamp: maxLines > 0 ? maxLines : undefined,
          textOverflow: 'ellipsis',
          overflow: 'hidden',
        }}
      >
        {currentText}
      </div>
    </Box>
  );
};
